import React, { useEffect, useContext, useState } from 'react';
import flexsearch from 'flexsearch';

import useSWR from 'swr';

import { AppProvider } from '../../context/AppProvider';
import { MasteryType, fetchMastery } from '../../lib/fetchMastery';

import { MAX_MASTERY_COUNT } from '../../constants';

import MasterySkeleton from './MasterySkeleton';
import { Search } from 'lucide-react';
import Table from '../Shared/Table';

const Filters = React.memo(
  ({ setType, type = '' }: { setType: React.Dispatch<React.SetStateAction<string>>; type: string }) => {
    return (
      <div className='px-3 rounded-md w-1/2 shadow-md max-sm:w-full bg-[#fbffff]'>
        <select className='select-box outline-none py-5 max-sm:py-3 w-full bg-[#fbffff]' onChange={(e) => setType(e.target.value)} defaultValue={type}>
          <option value=''>All Grades </option>
          <option value='4th_54'>4th grade - Saxon 5/4 or Intermediate 4</option>
          <option value='5th_65'>5th grade - Saxon 6/5 or Intermediate 5</option>
          <option value='6th_76'>6th grade - Saxon 7/6 or Course 1</option>
          <option value='7th_87'>7th grade - Saxon 8/7 or Course 2</option>
          <option value='7th_8th'>7th/8th grade - Algebra 1/2 or Course 3</option>
          <option value='algebra1'>Algebra 1</option>
          <option value='algebra2'>Algebra 2</option>
          <option value='geometry'>Geometry</option>
          <option value='advanced_math'>Advanced Math</option>
          <option value='beyond_advanced_math'>Beyond Advanced Math</option>
        </select>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.type == nextProps.type;
  }
);

const MasteryDashboard = () => {
  const { token, student_id } = useContext(AppProvider);
  const [results, setResults] = useState<MasteryType[]>([]);
  const [type, setType] = useState('');
  const [index, setIndex] = useState(
    new flexsearch.Index({
      preset: 'performance',
      tokenize: 'full',
      cache: true,
      optimize: true,
    })
  );

  const [query, setQuery] = useState('');

  const { data: response, isLoading, error } = useSWR([`/wp-json/quiz-leaderboard/v1/mastered-details/${student_id}`, token, type], ([path, token, type]) => fetchMastery(path, token, type), { revalidateOnFocus: false });

  useEffect(() => {
    response?.data.forEach((item) => {
      index.add(item.quiz_id, item.title);
    });
    setIndex(index);
  }, [index, response, type]);

  useEffect(() => {
    if (index && response?.data) {
      if (!query.trim()) {
        setResults(response.data || []);
      } else {
        const searchIds = index.search(query);
        const matchedResults = response?.data?.filter((item) => searchIds.includes(item.quiz_id));
        setResults(matchedResults);
      }
    }
  }, [index, query, response?.data]);

  if (isLoading) return <MasterySkeleton />;

  if (error) return <p>Error...</p>;

  return (
    <>
      <div className='mastery-dashboard-top flex justify-between items-center max-w-7xl gap-4 mx-auto mt-6 mb-10 px-5 max-sm:flex-col'>
        <div className='flex w-1/2 max-sm:w-full items-center border border-gray-50 rounded-md shadow-md px-6 bg-[#fbffff]'>
          <Search className=' absolute stroke-slate-400' />
          <input value={query} onChange={(e) => setQuery(e.target.value)} className='border-0 py-4 max-sm:py-3 w-full px-10 outline-none relative bg-transparent' placeholder='Search Topic' />
        </div>
        <Filters setType={setType} type={type} />
      </div>
      <Table MAX_MASTERY_COUNT={MAX_MASTERY_COUNT} results={results} />
    </>
  );
};

export default MasteryDashboard;
